import { AppShell, MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Header } from "./components/layout/Header";
import { ROUTES } from "./constants/routes";
import { CVList } from "./features/cv-list";
import CVEditor from "./features/CVEditor/CVEditor";
import { queryClient } from "./lib/queryClient";
import CVDetailPage from "./pages/CVDetailPage";
import { theme } from "./theme";

import { useState } from "react";

import { OperationsRoute } from "./auth/OperationsRoute";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { RoleBasedRoute } from "./auth/RoleBasedRoute";
import { AuthErrorBoundary } from "./components/common/AuthErrorBoundary";
import { AuthErrorLogger } from "./components/common/AuthErrorLogger";
import { FloatingChat } from "./components/common/FloatingChat";
import { NotificationRefreshProvider } from "./context/NotificationRefreshContext";
import { PersonFilterProvider } from "./context/PersonFilterContext";
import { SearchConfigProvider } from "./context/SearchConfigContext";
import { ValidationProvider } from "./context/ValidationContext";
import { EmployeeOverview } from "./features/employee-overview";
import {
  CertificatesFaqPage,
  Faq,
  FaqEmptyState,
  ProfessionalSummaryFaqPage,
  ProfileCompletionFaqOverview,
  SearchFaqPage,
  SkillsMatrixFaqPage,
  WorkVsProjectExperienceFaqPage,
} from "./features/faq";
import { CertificatesReport, Reports, TechnologyReport } from "./features/reports";
import { ChatAboutCandidatesPage } from "./pages/admin/ChatAboutCandidate";

import { OperationsAIManagement } from "./pages/operations/AIManagement";
import { OperationsCertificatesManagement } from "./pages/operations/CertificatesManagement";
import { OperationsCompaniesDictionary } from "./pages/operations/CompaniesDictionary";
import { OperationsContractTypesManagement } from "./pages/operations/ContractTypesManagement";
import { OperationsCostCentersManagement } from "./pages/operations/CostCentersManagement";
import { OperationsCVManagement } from "./pages/operations/CVManagement";
import { OperationsData } from "./pages/operations/Data";
import { OperationsDictionaryOverview } from "./pages/operations/DictionaryOverview";
import { OperationsEducationDictionaries } from "./pages/operations/EducationDictionaries";
import { OperationsEmployeeHub } from "./pages/operations/EmployeeHub";
import { OperationsFilters } from "./pages/operations/Filters";
import { OperationsLanguageDictionaries } from "./pages/operations/LanguageDictionaries";
import { OperationsNotificationTesting } from "./pages/operations/NotificationTesting";
import { OperationsOfficeLocations } from "./pages/operations/OfficeLocations";
import { OperationsPanel } from "./pages/operations/OperationsPanel";
import { OperationsPositionsManagement } from "./pages/operations/PositionsManagement";
import { OperationsProfileCompletionConfig } from "./pages/operations/ProfileCompletionConfig";
import { OperationsProjectsManagement } from "./pages/operations/ProjectsManagement";
import { OperationsRolesDictionary } from "./pages/operations/RolesDictionary";
import { OperationsTeams } from "./pages/operations/Teams";
import { OperationsTechnologiesManagement } from "./pages/operations/TechnologiesManagement";
import { OperationsTemplates } from "./pages/operations/Templates";
import { OperationsUsers } from "./pages/operations/Users";
import { OperationsWorkSchedulesManagement } from "./pages/operations/WorkSchedulesManagement";

import { OpportunityBoardPage } from "./features/opportunities/pages/OpportunityBoardPage";
import { OpportunityDetailPage } from "./features/opportunities/pages/OpportunityDetailPage";
import { OpportunityFormPage } from "./features/opportunities/pages/OpportunityFormPage";
import { UserRole } from "./models/user.model";
import { ChangelogPage } from "./pages/ChangelogPage";
import { HomePage } from "./pages/HomePage";
import { MySkills } from "./pages/MySkills";
import { PersonSkills } from "./pages/PersonSkills";
import { ProfilePage } from "./pages/ProfilePage";
import { RoleDemoPage } from "./pages/RoleDemoPage";

function AppContent() {
  const location = useLocation();
  const [chatMessages, setChatMessages] = useState<any[]>([]);

  return (
    <>
      <Routes>
        <Route
          path={ROUTES.HOME}
          element={
            <AppShell header={{ height: 60 }} padding="md">
              <AppShell.Header>
                <Header />
              </AppShell.Header>
              <AppShell.Main>
                <HomePage />
              </AppShell.Main>
            </AppShell>
          }
        />

        <Route
          path={ROUTES.CV_LIST}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <CVList />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.ROLE_DEMO}
          element={
            <ProtectedRoute>
              <RoleDemoPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CHANGELOG}
          element={
            <ProtectedRoute>
              <ChangelogPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CV_DETAILS_PATTERN}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <CVDetailPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CV_EDITOR}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <CVEditor />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CV_EDITOR_PATTERN}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <CVEditor />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CHAT}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <ChatAboutCandidatesPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CHAT_ABOUT_CANDIDATES}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <ChatAboutCandidatesPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Employee Overview — main nav, Admin+Manager only (FS_04_EmployeeHub_06) */}
        <Route
          path={ROUTES.EMPLOYEES}
          element={
            <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <EmployeeOverview />
                </AppShell.Main>
              </AppShell>
            </RoleBasedRoute>
          }
        />

        {/* Reports — main nav, Admin+Manager only (FS_05_Reporting_01, same precedent as Employee Overview) */}
        <Route
          path={ROUTES.REPORTS}
          element={
            <OperationsRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <Reports />
                </AppShell.Main>
              </AppShell>
            </OperationsRoute>
          }
        >
          <Route
            index
            element={<Navigate to={ROUTES.REPORTS_WITH_PATH("technology")} replace />}
          />
          <Route path="technology" element={<TechnologyReport />} />
          <Route path="certificates" element={<CertificatesReport />} />
        </Route>

        {/* FAQ — main nav, all roles (FS_07_FAQSystem_01) */}
        <Route
          path={ROUTES.FAQ}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <Faq />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        >
          <Route index element={<FaqEmptyState />} />
          <Route path="profile-completion" element={<ProfileCompletionFaqOverview />} />
          <Route path="search" element={<SearchFaqPage />} />
          <Route path="certificates" element={<CertificatesFaqPage />} />
          <Route path="skills-matrix" element={<SkillsMatrixFaqPage />} />
          <Route path="professional-summary" element={<ProfessionalSummaryFaqPage />} />
          <Route path="work-vs-project-experience" element={<WorkVsProjectExperienceFaqPage />} />
        </Route>

        {/* New Operations Panel Routes */}
        <Route
          path={ROUTES.OPERATIONS}
          element={
            <OperationsRoute>
              <OperationsPanel />
            </OperationsRoute>
          }
        >
          {/* Default operations route redirects to CV management */}
          <Route
            index
            element={
              <Navigate
                to={ROUTES.OPERATIONS_WITH_PATH("cv-management")}
                replace
              />
            }
          />

          {/* CV Management - Now accessible to Managers */}
          <Route
            path="cv-management"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsCVManagement />
              </RoleBasedRoute>
            }
          />

          {/* User Management - Admin only */}
          <Route
            path="users"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsUsers />
              </RoleBasedRoute>
            }
          />

          {/* Dictionaries - Admin and Manager */}
          <Route
            path="dictionaries"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsDictionaryOverview />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/certificates"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsCertificatesManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/technologies"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsTechnologiesManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/education"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsEducationDictionaries />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/languages"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsLanguageDictionaries />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/roles"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsRolesDictionary />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/companies"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsCompaniesDictionary />
              </RoleBasedRoute>
            }
          />

          <Route
            path="dictionaries/officelocations"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsOfficeLocations />
              </RoleBasedRoute>
            }
          />

          {/* Data Management - Admin only */}
          <Route
            path="data"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsData />
              </RoleBasedRoute>
            }
          />

          {/* Notification Testing - Admin only */}
          <Route
            path="notification-testing"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsNotificationTesting />
              </RoleBasedRoute>
            }
          />

          {/* AI Management - Admin only */}
          <Route
            path="ai-management"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsAIManagement />
              </RoleBasedRoute>
            }
          />

          {/* Employee Hub - Admin only */}
          <Route
            path="employee-hub"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsEmployeeHub />
              </RoleBasedRoute>
            }
          />

          <Route
            path="employee-hub/dictionaries/cost-centers"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsCostCentersManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="employee-hub/dictionaries/projects"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsProjectsManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="employee-hub/dictionaries/work-schedules"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsWorkSchedulesManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="employee-hub/dictionaries/positions"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsPositionsManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="employee-hub/dictionaries/contract-types"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsContractTypesManagement />
              </RoleBasedRoute>
            }
          />

          {/* Future tabs (when enabled) */}
          <Route
            path="teams"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsTeams />
              </RoleBasedRoute>
            }
          />

          <Route
            path="templates"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsTemplates />
              </RoleBasedRoute>
            }
          />

          <Route
            path="filters"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin]}>
                <OperationsFilters />
              </RoleBasedRoute>
            }
          />

          <Route
            path="profile-completion-config"
            element={
              <RoleBasedRoute allowedRoles={[UserRole.Admin, UserRole.Manager]}>
                <OperationsProfileCompletionConfig />
              </RoleBasedRoute>
            }
          />
        </Route>

        {/* Talent Marketplace */}
        <Route
          path={ROUTES.OPPORTUNITIES}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <OpportunityBoardPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.OPPORTUNITY_NEW}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <OpportunityFormPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.OPPORTUNITY_EDIT_PATTERN}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <OpportunityFormPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.OPPORTUNITY_DETAIL_PATTERN}
          element={
            <ProtectedRoute>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Header />
                </AppShell.Header>
                <AppShell.Main>
                  <OpportunityDetailPage />
                </AppShell.Main>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.MY_SKILLS}
          element={
            <ProtectedRoute>
              <MySkills />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.PERSON_SKILLS_PATTERN}
          element={
            <ProtectedRoute>
              <PersonSkills />
            </ProtectedRoute>
          }
        />

      </Routes>

      {!location.pathname.includes("chat-about-candidates") &&
        location.pathname !== "/" && (
          <FloatingChat
            initialMessages={chatMessages}
            onMessagesChange={setChatMessages}
          />
        )}
      <AuthErrorLogger />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <Notifications position="top-right" zIndex={1000} />
        <AuthErrorBoundary>
          <ValidationProvider>
            <NotificationRefreshProvider>
              <PersonFilterProvider>
                <SearchConfigProvider>
                  <BrowserRouter>
                    <AppContent />
                  </BrowserRouter>
                </SearchConfigProvider>
              </PersonFilterProvider>
            </NotificationRefreshProvider>
          </ValidationProvider>
        </AuthErrorBoundary>
      </MantineProvider>
    </QueryClientProvider>
  );
}
